<template>
  <div class="debugger flexColumn">
    <h3 class="ui dividing header">{{ $t('DEBUGGER') }}</h3>
    <div class="ui flexOne flexColumn">
      <div class="debugSettingsSection ui accordion scroller ">
        <div
          class="title"
          :class="{ active: showDebugSettings }"
          @click="showDebugSettings = !showDebugSettings"
        >
          <i class="dropdown icon"></i>
          {{ $t('SETTINGS') }}
        </div>
        <div class="content" :class="{ active: showDebugSettings }">
          <div class="ui form">
            <fieldset :disabled="areDebugSettingsDisabled">
              <div class="fields">
                <div class="seven wide field">
                  <label for="debugger-host">{{ $t('WEBSOCKET_HOST') }}</label>
                  <input
                    id="debugger-host"
                    class="monospaced"
                    type="text"
                    v-model="host"
                    @input="areDebuggerConnectionSettingsEdited = true"
                  />
                </div>
                <div class="seven wide field">
                  <label for="debugger-port">{{ $t('WEBSOCKET_PORT') }}</label>
                  <input
                    id="debugger-port"
                    class="monospaced"
                    type="number"
                    v-model.number="port"
                    @input="areDebuggerConnectionSettingsEdited = true"
                  />
                </div>
              </div>

              <div class="field togglefield">
                <div class="ui toggle checkbox">
                  <input
                    type="checkbox"
                    class="hidden"
                    :checked="secureWs"
                    @change="setSecure($event.currentTarget.checked)"
                    id="debugger-secure-ws"
                  />
                  <label for="debugger-secure-ws">
                    {{ $t('WEBSOCKET_SECURE') }}
                  </label>
                </div>
              </div>

              <div class="field">
                <button type="button" class="ui green button" @click="connectWithSettings">
                  {{ $t('APPLY_SETTINGS') }}
                </button>
              </div>
              <div class="two wide field">
                <label for="delay">{{ $t('DELAY') }}</label>
                <input id="delay" type="number" :placeholder="$t('DELAY')" v-model.number="delay" />
              </div>
              <div class="eight wide field">
                <div class="field">
                  <label for="inputs">{{ $tc('INPUT', 2) }}</label>
                  <key-value-input id="inputs" :keys="possibleInputs" :add-handler="addInput" />
                </div>
                <key-value-item-list
                  :items="addedInputs"
                  :removeHandler="removeInput"
                  :disabled="areDebugSettingsDisabled"
                />
              </div>
            </fieldset>
          </div>
        </div>
      </div>
      <div class="ui clearing divider" />
      <div id="console" class="ui flexOne flexColumn inverted segment">
        <div class="ui inverted menu">
          <div class="ui icon black buttons">
            <button type="button" class="ui button" @click="clearLog()">
              <i class="trash alternate outline icon" :title="$t('CLEAR_CONSOLE')"></i>
            </button>
            <button type="button" class="ui button" @click="copyLogToClipboard()">
              <i
                class="copy outline icon"
                :title="$t('COPY_TO_CLIPBOARD')"
                @click="copyLogToClipboard"
              ></i>
            </button>
            <button type="button" class="ui button" @click="exportLogToFile()">
              <i class="download icon" :title="$t('EXPORT_TO_JSON')"></i>
            </button>
          </div>
          <a ref="downloadLink" href="#" style="display: none;"></a>
        </div>
        <div class="ui inverted segment flexOne scroller" ref="consoleWindow">
          <div class="ui warning message" v-if="logs.length == maxLines">
            {{ $t('LOG_TRUNCATED', { maxLines: maxLines }) }}
          </div>
          <div v-for="l in formattedLogs" :key="l.ts">
            <div
              v-if="l.log !== 'RUN'"
              class="monospaced"
              :class="{
                errorLog: isError(l.log),
                warningLog: isWarning(l.log),
                infoLog: isInfo(l.log),
                debugLog: isDebug(l.log)
              }"
            >
              <span class="ts">{{ l.ts }}</span>
              <span class="log">{{ l.log }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../../scss/variables';

.ts {
  opacity: 0.6;
}

.container {
  overflow: scroll;
}

.debugger {
  height: 100%;
  width: 100%;
  position: relative;
}

.scroller {
  overflow-y: auto;
  overflow-x: auto;
  max-height: 100%;
}

#console .scroller {
  flex: 1 0 50px;
  padding: 0;
  margin: 0;
}

#debugSettingsSection {
  max-height: 30%;
}

.errorLog {
  color: $red;
}
.warningLog {
  color: $yellow;
}
.infoLog {
  color: $w;
}
.debugLog {
  color: $grey;
}

.flexOne {
  flex: 1;
}

.logRow {
  max-height: 2em;
  min-height: 2em;
}

.flexColumn {
  display: flex;
  flex-direction: column;
}

hr {
  margin: 10px;
  margin-right: 10px;
}
</style>
<script>
// @flow
import Vue from 'vue'
import { mapFields } from 'vuex-map-fields'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { FlowStateEnum } from 'enums'
import { KeyValueInput, KeyValueItemList } from 'ui'
import _ from 'lodash'
import { conf, Toast, File } from 'services'
import copy from 'clipboard-copy'
import papa from 'papaparse'
import columnify from 'columnify'

export default {
  name: 'debugger',
  components: {
    KeyValueInput,
    KeyValueItemList
  },
  data() {
    return {
      isShown: false,
      delay: 0,
      maxLines: 1000,
      addedInputs: [],
      host: conf.manateeHost(),
      port: 0,
      secureWs: false,
      showDebugSettings: false,
      areDebuggerConnectionSettingsEdited: false,
      isDebuggerEnabled: !conf.disableDebugger,
      userDisconnected: false
    }
  },
  computed: {
    ...mapFields('debugger', ['flowDebugState']),
    logs() {
      return _.takeRight(this.$store.state.debugger.logs, this.maxLines)
    },
    flow() {
      return this.$store.state.flowEditor.flow
    },
    flowDebugState() {
      return this.$store.state.debugger.flowDebugState
    },
    debugSocket() {
      return this.$store.state.debugger.debugSocket
    },
    debugSocketConf() {
      return this.$store.state.debugger.debugSocketConf
    },
    possibleInputs() {
      return (this.flow.inputs || [])
        .map(i => i.name)
        .filter(i => !_.includes(this.addedInputs.map(a => a.key), i))
    },
    areDebugSettingsDisabled() {
      return this.flowDebugState !== FlowStateEnum.STOPPED
    },
    formattedLogs() {
      return this.logs.map(l => {
        return { ...l }
      })
    }
  },
  methods: {
    ...mapMutations('debugger', ['clearLog', 'setDebugInputs', 'setDebugDelay']),
    ...mapActions('debugger', ['openDebugSocket', 'closeDebugSocket']),
    getFlowState(stateNumber) {
      return Object.keys(FlowStateEnum)[stateNumber]
    },
    addInput(input) {
      const exists = this.addedInputs.some(i => i.key === input.key)
      if (!exists) this.addedInputs = [...this.addedInputs, input]
    },
    removeInput(input) {
      this.addedInputs = this.addedInputs.filter(i => i.key !== input.key)
    },

    setSecure(s) {
      this.secureWs = s
      this.port = this.secureWs ? this.debugSocketConf.securePort : this.debugSocketConf.port
    },

    scrollConsole() {
      if (this.$refs.consoleWindow)
        this.$refs.consoleWindow.scrollTop = this.$refs.consoleWindow.scrollHeight
    },
    connectWithSettings() {
      this.closeDebugSocket()
      this.connectDebugger()
    },
    connectDebugger() {
      this.openDebugSocket({
        host: this.host,
        port: this.port,
        securePort: this.secureWs ? this.port : 0
      })
      if (this.debugSocket) {
        const showDebugErrorToastOnce = () => {
          Toast.error(this.$i18n.t('DEBUGGER_CONNECTION_ERROR'))
          if (this.debugSocket) this.debugSocket.off('error', showDebugErrorToastOnce)
        }
        this.debugSocket.on('error', showDebugErrorToastOnce)
      }
    },
    generateCsv() {
      return papa.unparse(this.formattedLogs, {
        header: true,
        delimiter: ',',
        quotes: true
      })
    },
    copyLogToClipboard() {
      copy(this.generateCsv()).then(Toast.success('Logs copied to  clipboard'))
    },
    exportLogToFile() {
      File.downloadFile(this.generateCsv(), 'logs.csv', 'text/csv', this.$refs.downloadLink)
    },
    isWarning(log) {
      return log && log.toLowerCase().startsWith('log.warn')
    },
    isInfo(log) {
      return log && log.toLowerCase().startsWith('log.info')
    },
    isError(log) {
      return log && log.toLowerCase().startsWith('log.error')
    },
    isDebug(log) {
      return !log || (!this.isWarning(log) && !this.isInfo(log) && !this.isError(log))
    },
    initSocketConfInputs() {
      this.host = this.debugSocketConf.host || 'localhost'
      this.port = this.debugSocketConf.securePort || conf.defaultWebsocketPortSecure()
      this.secureWs = !!this.port
      if (!this.port) {
        this.port = this.debugSocketConf.port || conf.defaultWebsocketPort()
      }
    }
  },

  updated() {
    this.scrollConsole()
  },

  created() {
    this.initSocketConfInputs()
  },

  beforeDestroy() {
    this.closeDebugSocket()
  },

  watch: {
    addedInputs: function() {
      this.setDebugInputs(this.addedInputs)
    },
    delay: function() {
      this.setDebugDelay(this.delay)
    },
    debugSocketConf: {
      handler(newConf) {
        this.initSocketConfInputs()
      },
      deep: true
    }
  }
}
//
</script>
